import type React from 'react';
import { type ReactElement, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import getConfig from 'next/config';

import { fetchResults } from '@/state/resultActions';
import { actions as filterActions } from '@/state/filterActions';

import type { ApiQueryParams } from '@/interfaces';
import { Autocomplete } from '@/components/autocomplete/Autocomplete';

const {
    publicRuntimeConfig: { autocompleteUrl, assetPrefix },
} = getConfig();

interface Props {
    triggerFetch: (queryParam?: ApiQueryParams) => void;
    addDestination: (destination: string) => void;
}

interface DestinationFromBackend {
    id: string;
    name: string;
}

interface AutocompleteDestination {
    id: string;
    text: string;
}

export const DestinationDropdown: React.FC<Props> = ({ triggerFetch, addDestination }): ReactElement<Props> => {
    const [autocompleteText, setAutocompleteText] = useState('');

    useEffect((): void => {
        const destinationPlaceholder = document.getElementById('destination');
        destinationPlaceholder?.setAttribute('placeholder', 'Legg til destinasjon');
    }, []);

    const addDestinationAndFetch = (destination: AutocompleteDestination): void => {
        addDestination(destination.id);
        triggerFetch();
    };

    const onChange = (destination: AutocompleteDestination): void => {
        if (destination) {
            setAutocompleteText('');
            addDestinationAndFetch(destination);
        }
    };

    const destinationMapper = (destinations: DestinationFromBackend[]): AutocompleteDestination[] =>
        destinations.map((location) => ({
            id: location.id,
            text: location.name,
        }));

    const translate = (key: string): string => ('common:action.cancel' === key ? 'Avbryt' : key);

    return (
        <div className="input u-mb0 input--select">
            <label htmlFor="location">Reis til</label>
            <div className="input--select__wrap destination-autocomplete" data-testid="destinationDropdown">
                <Autocomplete
                    id="destination"
                    value={autocompleteText}
                    mapResults={destinationMapper}
                    onChange={onChange}
                    onChangeText={setAutocompleteText}
                    assetUrl={`${assetPrefix}/`}
                    baseUrl={autocompleteUrl}
                    t={translate}
                    minChars={2}
                    emptyOptionsOnChange
                />
            </div>
        </div>
    );
};

const mapDispatchToProps = {
    addDestination: filterActions.addDestination,
    triggerFetch: fetchResults,
};

export default connect(undefined, mapDispatchToProps)(DestinationDropdown);
